import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

export interface Request {
  id: string;
  schoolId: string;
  schoolName: string;
  contactPerson?: string;
  phone?: string;
  dates: string[];
  notes: string;
  status: 'pending' | 'accepted' | 'declined';
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

const API_URL = process.env.REACT_APP_API_URL || '';

export function useRequests() {
  const { token, user } = useAuth();
  const [requests, setRequests] = useState<Request[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const socketRef = useRef<Socket | null>(null);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Setup socket connection for real-time updates
  useEffect(() => {
    if (!token) return;

    const socketUrl = API_URL || window.location.origin;
    const socket = io(socketUrl);
    socketRef.current = socket;

    socket.on('new-request', (request: Request) => {
      setRequests(prev => [request, ...prev]);
    });

    socket.on('request-updated', (updated: Request) => {
      setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
    });

    socket.on('request-deleted', ({ id }: { id: string }) => {
      setRequests(prev => prev.filter(r => r.id !== id));
    });

    fetchRequests();

    return () => {
      socket.disconnect();
    };
  }, [token, fetchRequests]);

  // Create a new request (for schools)
  const createRequest = async (dates: string[], notes?: string) => {
    if (!token) return;

    const response = await fetch(`${API_URL}/api/requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ dates, notes })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create request');
    }

    return response.json();
  };

  // Update request status (for admin)
  const updateRequestStatus = async (
    requestId: string, 
    status: 'pending' | 'accepted' | 'declined',
    adminNotes?: string
  ) => {
    if (!token) return;

    const response = await fetch(`${API_URL}/api/requests/${requestId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ status, adminNotes })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update request');
    }

    return response.json();
  };

  // Delete a request
  const deleteRequest = async (requestId: string) => {
    if (!token) return;

    const response = await fetch(`${API_URL}/api/requests/${requestId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete request');
    }

    return response.json();
  };

  return {
    requests,
    isLoading,
    createRequest,
    updateRequestStatus,
    deleteRequest,
    refetch: fetchRequests
  };
}
